import { PropsWithChildren, useState } from "react";
import BuildIcon from "@mui/icons-material/Build";
import CloseIcon from "@mui/icons-material/Close";
import PreviewIcon from "@mui/icons-material/Preview";
import PreviewIconActive from "@mui/icons-material/PreviewTwoTone";
import InspectIcon from "@mui/icons-material/PageviewOutlined";
import InspectIconActive from "@mui/icons-material/PageviewTwoTone";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";

import { BaseButton } from "@/src/component/base/BaseButton";
import { ElementInspector, ElementInfo } from "@/src/component/partial/__internal__/ElementInspector";
import { GrowthBookType } from "@/src/type/growthbook";

import { AnalyticsPanel, VariantTestingPanel } from "./component";

import styles from "./ToolKit.module.scss";
import theme from "@/styles/scss/theme.module.scss";
import ToolkitSelector from "./component/ToolkitSelector";

interface ToolKitProps {
  enable?: boolean;
  growthBook?: GrowthBookType.GrowthBookAndContentVariants;
}

function ToolKit({ children, enable, growthBook }: PropsWithChildren<ToolKitProps>) {
  const [toolkitActive, setToolkitActive] = useState<"drawer" | "inline" | "float" | "off">("off");
  const [navigationOption, setNavigationOption] = useState<"analytics" | "variants">("variants");
  const [elementInspectorActive, setElementInspectorActive] = useState<boolean>(false);
  const [selectedElement, setSelectedElement] = useState<HTMLElement | null>(null);
  const [allActive, setAllActive] = useState<boolean>(false);
  const [allElements, setAllElements] = useState<{ element: HTMLElement; info: ElementInfo }[]>([]);

  /**
   * when the user clicks a tab button switch what is render and update the url
   */
  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setNavigationOption(newValue as "analytics" | "variants");
  };

  const handleElementInspector = (on: boolean) => {
    setElementInspectorActive(on);
    setAllActive(false);
    if (!on) {
      //setSelectedElement(null);
    }
  };

  const handleAllElementsActive = (on: boolean) => {
    setAllActive(on);
    setElementInspectorActive(false);
    if (!on) {
      //setSelectedElement(null);
    }
  };

  const handleSelectElement = (element: HTMLElement) => {
    setSelectedElement(element);
    setAllActive(false);
    setElementInspectorActive(true);
  };

  if (!enable) {
    return <>{children}</>;
  }

  return (
    <>
      <div className={styles.launch}>
        {toolkitActive === "off" && (
          <BaseButton
            variant="contained"
            color="primary"
            onClick={() => setToolkitActive("inline")}
          >
            <BuildIcon />
          </BaseButton>
        )}
      </div>
      {toolkitActive === "off" && <>{children}</>}
      {toolkitActive == "inline" && (
        <div className={styles.container}>
          <div
            className={styles.content}
            id="toolkitInspector"
          >
            {children}
            <ElementInspector
              isActive={elementInspectorActive}
              allActive={allActive}
              onSelectElement={handleSelectElement}
              setIsActive={handleElementInspector}
              setAllActive={handleAllElementsActive}
              setAllElements={setAllElements}
            />
          </div>
          <div className={styles.toolkit}>
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <ToolkitSelector />
                <IconButton
                  onClick={() => setAllActive(!allActive)}
                  size="small"
                >
                  {allActive ? <PreviewIconActive /> : <PreviewIcon />}
                </IconButton>
                <IconButton
                  onClick={() => handleElementInspector(!elementInspectorActive)}
                  size="small"
                >
                  {elementInspectorActive && !allActive ? <InspectIconActive /> : <InspectIcon />}
                </IconButton>
                <IconButton
                  onClick={() => setToolkitActive("off")}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </div>
              <div className={styles.cardContent}>
                <Tabs
                  orientation="horizontal"
                  value={navigationOption}
                  onChange={handleChange}
                  aria-label="Navigation Options"
                  variant="scrollable"
                  scrollButtons="auto"
                  visibleScrollbar={false}
                  allowScrollButtonsMobile={true}
                  sx={{
                    ".MuiTabs-indicator": {
                      left: 0,
                      width: "4px",
                    },
                    ".MuiTabs-fixed": {
                      borderBottom: `1px solid ${theme.primaryMain}33`,
                    },
                    ".MuiTabs-flexContainer button": {
                      width: "50%",
                    },
                  }}
                >
                  <Tab
                    value="variants"
                    label={
                      <Typography
                        variant="body2"
                        fontFamily={"var(--roughwell)"}
                      >
                        A/B Testing
                      </Typography>
                    }
                  />
                  <Tab
                    value="analytics"
                    label={
                      <Typography
                        variant="body2"
                        fontFamily={"var(--roughwell)"}
                      >
                        Analytics
                      </Typography>
                    }
                  />
                </Tabs>
                {navigationOption === "analytics" && <AnalyticsPanel />}
                {navigationOption === "variants" && (
                  <VariantTestingPanel
                    selectedElement={selectedElement}
                    elementInspectorActive={elementInspectorActive}
                    allActive={allActive}
                    growthBook={growthBook}
                    allElements={allElements}
                    setSelectedElement={handleSelectElement}
                    setElementInspectorActive={handleElementInspector}
                    setAllActive={handleAllElementsActive}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ToolKit;
